namespace Portico.Services {
    import pme = Portico.Models.Enterprise;
    import psga = apigateway.client;
    import psgm = apigateway.models;
    import Inject = Portico.Decorators.Inject;
    import Service = Portico.Decorators.Service;

    export interface ISiteAccountService {
        cancelWorkflow(patientAccountId: number, workflowId: number): angular.IPromise<any>;
        getAccount(accountId: number): angular.IPromise<pme.Account>;
		getCase(caseId: number): angular.IPromise<psgm.GetCaseResponseModel>;
		getCases(accountId: number): angular.IPromise<psgm.CaseModel[]>;
        getParty(): angular.IPromise<pme.Site>;
        getSiteAccount(): angular.IPromise<pme.SiteAccount>;
		getSiteAccounts(portalId: string): angular.IPromise<pme.SiteAccount[]>;
        getWorkflows(
            accountId: number,
            includePayors: boolean,
            includePharmacies: boolean,
            includePrescribers: boolean
        ): angular.IPromise<psgm.WorkflowModel[]>;
        searchAccounts(account: pme.Account): angular.IPromise<pme.Account[]>;
        searchCases(searchCasesRequest: psgm.SearchCasesRequestModel): angular.IPromise<psgm.SearchCasesResponseModel[]>;
        searchWorkflows(
            searchWorkflowsRequest: psgm.SearchWorkflowsRequestModel
        ): angular.IPromise<psgm.SearchWorkflowsResponseModel[]>;
        siteRegistration: (
            siteAccountRegistration: pme.SiteAccountRegistration
        ) => angular.IPromise<psgm.ISiteRegistrationData>;
    }

    @Service('app', 'siteAccountService')
	@Inject('$q', 'apiService', 'DocumentV3Api', 'EdgeSiteAccountApi')
    export class SiteAccountService {
        constructor(
            private $q: angular.IQService,
			private apiService: IApiService,
            private documentApi: psga.DocumentV3Api,
            private edgeSiteAccountApi: psga.EdgeSiteAccountApi
        ) {}

        cancelWorkflow(patientAccountId: number, workflowId: number, caseTypeStatusRelationshipId: number, reason: string) {
            return this.edgeSiteAccountApi
                .siteAccountCancelWorkflow(
                    {
                        patientAccountId,
                        workflowId,
                        caseTypeStatusRelationshipId,
                        reason            
                    } as psgm.CancelWorkflowRequestModel)
                .then(response => {
                    return response;
                });
        }

        getAccount(accountId: number): angular.IPromise<pme.Account> {
            return this.$q.when(this.edgeSiteAccountApi
                .siteAccountGetAccount({ accountId: accountId })
                .then(response => {
                    response.data.data.account.lastModifiedTime = <moment.Moment><unknown>response.data.data.account.lastModifiedTime?.toDate();
                    response.data.data.patient.dateOfBirth = <moment.Moment><unknown>response.data.data.patient.dateOfBirth?.toDate();
                    const account = new pme.Account(response.data.data.account);
                    account.patient = new pme.Patient(response.data.data.patient);
                    return account;
                }));
        }

		getCase(caseId: number): angular.IPromise<psgm.GetCaseResponseModel> {
			var request: psgm.GetCaseRequestModel = {
				caseId: caseId,
				includeDiagnoses: true,
				includePayors: true,
				includePharmacies: true,
				includePrescribers: true,
				includePrescriptions: true,
				includeSites: true,
				includeSurveys: true
			};
            return this.$q.when(this.edgeSiteAccountApi.siteAccountGetCase(request).then(response => {
                response.data.data.accountData.lastModifiedTime = <moment.Moment><unknown>response.data.data.accountData.lastModifiedTime?.toDate();
                response.data.data.caseData.createdOn = <moment.Moment><unknown>response.data.data.caseData.createdOn?.toDate();
                response.data.data.caseData.endDate = <moment.Moment><unknown>response.data.data.caseData.endDate?.toDate();
                response.data.data.caseData.expirationDate = <moment.Moment><unknown>response.data.data.caseData.expirationDate?.toDate();
                response.data.data.caseData.startDate = <moment.Moment><unknown>response.data.data.caseData.startDate?.toDate();
                response.data.data.patientData.dateOfBirth = <moment.Moment><unknown>response.data.data.patientData.dateOfBirth?.toDate();
                response.data.data.payorData.forEach(p => {
                    p.benefitData.createDate = <moment.Moment><unknown>p.benefitData.createDate?.toDate();
                    p.benefitData.dateOfBirth = <moment.Moment><unknown>p.benefitData.dateOfBirth?.toDate();
                    p.benefitData.planEffectiveDate = <moment.Moment><unknown>p.benefitData.planEffectiveDate?.toDate();
                    p.benefitData.planExpirationDate = <moment.Moment><unknown>p.benefitData.planExpirationDate?.toDate();
                    p.benefitData.planTerminationDate = <moment.Moment><unknown>p.benefitData.planTerminationDate?.toDate();
                });
                response.data.data.prescriptionData.forEach(p => {
                    p.createdOn = <moment.Moment><unknown>p.createdOn?.toDate();
                    p.updatedOn = <moment.Moment><unknown>p.updatedOn?.toDate();
                    p.writtenOn = <moment.Moment><unknown>p.writtenOn?.toDate();
                });
                response.data.data.siteData.forEach(s => {
                    s.siteData.businessEndTime = <moment.Moment><unknown>s.siteData.businessEndTime?.toDate();
                    s.siteData.businessStartTime = <moment.Moment><unknown>s.siteData.businessStartTime?.toDate();
                });
                response.data.data.surveyData.forEach(s => {
                    s.sessionData.creationTime = <moment.Moment><unknown>s.sessionData.creationTime?.toDate();
                });
                return response.data.data;
			}));
		}

		getCases(accountId: number): angular.IPromise<psgm.CaseModel[]> {
			var request: psgm.GetCasesRequestModel = {
				accountId,
			};
            return this.$q.when(this.edgeSiteAccountApi.siteAccountGetCases(request).then(response => {
                response.data.data.forEach(c => {
                    c.createdOn = <moment.Moment><unknown>c.createdOn?.toDate();
                    c.endDate = <moment.Moment><unknown>c.endDate?.toDate();
                    c.expirationDate = <moment.Moment><unknown>c.expirationDate?.toDate();
                    c.startDate = <moment.Moment><unknown>c.startDate?.toDate();
                });
                return response.data.data;
			}));
		}

        getParty(): angular.IPromise<pme.Site> {
            return this.$q.when(this.edgeSiteAccountApi.siteAccountGetParty({}).then(response => {
                response.data.data.businessEndTime = <moment.Moment><unknown>response.data.data.businessEndTime?.toDate();
                response.data.data.businessStartTime = <moment.Moment><unknown>response.data.data.businessStartTime?.toDate();
                return new pme.Site(response.data.data);
            }));
        }

        getSiteAccount(): angular.IPromise<pme.SiteAccount> {
            return this.$q.when(this.edgeSiteAccountApi.siteAccountGetSiteAccount({}).then(response => {
                response.data.data.lastModifiedTime = <moment.Moment><unknown>response.data.data.lastModifiedTime?.toDate();
                return new pme.SiteAccount(response.data.data);
            }));
        }

		getSiteAccounts(portalId: string): angular.IPromise<pme.SiteAccount[]> {
			const endpoint = `/siteaccount/${portalId}`;

			return this.apiService.getItem<any[]>(endpoint).then(response => {
				return _.map(response, (sa) => {
					return new pme.SiteAccount(<pme.ISiteAccount>{
						id: sa.id,
						party: new pme.Site(<pme.ISite>{
							id: sa.site.id,
							description: sa.site.name,
							addresses: [
								new pme.Address(<pme.IAddress>{
									address1: sa.site.address1,
									address2: sa.site.address2,
									city: sa.site.city,
									state: sa.site.state,
									zip: sa.site.zip
								})
							],
							phoneNumbers: [
								new pme.PhoneNumber(<pme.IPhoneNumber>{
									number: sa.site.phone,
									type: 'PHONE'
								}),
								new pme.PhoneNumber(<pme.IPhoneNumber>{
									number: sa.site.fax,
									type: 'FAX'
								})
							]
						})
					});
				});
			});
		}

        getWorkflows(
            accountId: number,
            includePayors: boolean,
            includePharmacies: boolean,
            includePrescribers: boolean
        ): angular.IPromise<psgm.WorkflowModel[]> {
            var requestModel: psgm.GetWorkflowsRequestModel = {
                accountId,
                includePayors,
                includePharmacies,
                includePrescribers,
            };

            return this.$q.when(this.edgeSiteAccountApi.siteAccountGetWorkflows(requestModel).then(workflowResponse => {
                const promises: angular.IPromise<void>[] = [];

                // temporary solution
                for (const workflow of workflowResponse.data.data) {
                    for (const relationship of workflow.caseWorkflow.documents) {
                        // TODO: wrap in an edge call
                        const promise = this.$q.when(this.documentApi
                            .documentGetByProgramId(relationship.documentId)
                            .then(documentResponse => {
                                (<any>relationship).document = documentResponse.data.data;
                            }));

                        promises.push(promise);
                    }
                }

                return this.$q.all(promises).then(() => {
                    return workflowResponse.data.data;
                });
            }));
        }

        searchAccounts(account: pme.Account): angular.IPromise<pme.Account[]> {
            const request: psgm.SearchAccountsRequestModel = {
                firstName: _.get(account, 'patient.firstName', ''),
                lastName: _.get(account, 'patient.lastName', ''),
                dateOfBirth: _.get(account, 'patient.dateOfBirth', ''),
                city: _.get(account, 'patient.addresses[0].city', ''),
                state: _.get(account, 'patient.addresses[0].state', ''),
                zip: _.get(account, 'patient.addresses[0].zip', ''),
                gender: _.get(account, 'patient.gender', null),
            };

            return this.$q.when(this.edgeSiteAccountApi.siteAccountSearchAccounts(request).then(response => {
                const temp: pme.Account[] = [];

                for (const patientAccount of response.data.data) {
                    patientAccount.account.lastModifiedTime = <moment.Moment><unknown>patientAccount.account.lastModifiedTime?.toDate();
                    patientAccount.patient.dateOfBirth = <moment.Moment><unknown>patientAccount.patient.dateOfBirth?.toDate();
                    const account = new pme.Account(patientAccount.account);
                    account.patient = new pme.Patient(patientAccount.patient);
                    account.patient.ssn = patientAccount.ssn;
                    temp.push(account);
                }

                return temp;
            }));
        }

        searchCases(searchCasesRequest: psgm.SearchCasesRequestModel): angular.IPromise<psgm.SearchCasesResponseModel[]> {            
            return this.$q.when(this.edgeSiteAccountApi.siteAccountSearchCases(searchCasesRequest).then((response) => {
                response.data.data.forEach(r => {
                    r.intakeCase.createdOn = <moment.Moment><unknown>r.intakeCase.createdOn?.toDate();
                    r.intakeCase.endDate = <moment.Moment><unknown>r.intakeCase.endDate?.toDate();
                    r.intakeCase.expirationDate = <moment.Moment><unknown>r.intakeCase.expirationDate?.toDate();
                    r.intakeCase.startDate = <moment.Moment><unknown>r.intakeCase.startDate?.toDate();
                    r.workflows.forEach(w => {
                        w.patient.dateOfBirth = <moment.Moment><unknown>w.patient.dateOfBirth?.toDate();
                        w.workflow.createdOn = <moment.Moment><unknown>w.workflow.createdOn?.toDate();
                        w.workflow.endDate = <moment.Moment><unknown>w.workflow.endDate?.toDate();
                        w.workflow.expirationDate = <moment.Moment><unknown>w.workflow.expirationDate?.toDate();
                        w.workflow.startDate = <moment.Moment><unknown>w.workflow.startDate?.toDate();
                    });
                });
                return response.data.data;
			}));
        }

        searchWorkflows(searchWorkflowsRequest: psgm.SearchWorkflowsRequestModel) {
            return this.$q.when(this.edgeSiteAccountApi.siteAccountSearchWorkflows(searchWorkflowsRequest).then((response) => {
                response.data.data.forEach(w => {
                    w.patient.dateOfBirth = <moment.Moment><unknown>w.patient.dateOfBirth?.toDate();
                    w.workflow.createdOn = <moment.Moment><unknown>w.workflow.createdOn?.toDate();
                    w.workflow.endDate = <moment.Moment><unknown>w.workflow.endDate?.toDate();
                    w.workflow.expirationDate = <moment.Moment><unknown>w.workflow.expirationDate?.toDate();
                    w.workflow.startDate = <moment.Moment><unknown>w.workflow.startDate?.toDate();
                });
                return response.data.data;
            }));
        }

        siteRegistration(siteAccountRegistration: pme.SiteAccountRegistration) {
            const registration = new pme.SiteAccountRegistration(siteAccountRegistration);
            const request = {
                userName: registration.userName,
                firstName: registration.firstName,
                lastName: registration.lastName,
                phone: registration.phone,
                fax: registration.fax,
                email: registration.email,
                siteAccountUpsearch: registration.siteAccount.mapToEnterpriseCoordinatorSiteAccountUpsearchRequestModel(
                    true,
                    true,
                    false,
                    true,
                    true,
                    false
                ),
            } as psgm.ISiteRegistrationRequestModel;

            return this.edgeSiteAccountApi.siteAccountSiteRegistration(request).then(response => {
                return response.data.data;
            });
        }
    }
}
